/**
 * metadataVerificationService.ts — Verificação e auto-update de metadados
 *
 * Camada 3: detectMetadataStatus() — três níveis (critical/warning/complete)
 * Camada 1: verifyMetadata() + buildComparison()
 * Camada 2: verifyAndAutoFill() (completar apenas campos vazios)
 */

import { Work, type MetadataStatus } from '../../types/work';
import { fetchByISBN } from '../metadataFetcher';
import { searchByDOI as crossrefSearchByDOI, searchByAuthorYearTitle } from '../capturaInteligente/crossrefService';
import { searchByTitleAuthor as openAlexSearchByTitleAuthor } from '../capturaInteligente/openAlexService';
import type { WorkCandidate } from '../../types/capturaInteligente';

// ============================================================================
// TIPOS
// ============================================================================

export interface MetadataComparison {
  source: string;
  fields: ComparisonField[];
}

export interface ComparisonField {
  key: string;
  label: string;
  currentValue: string | undefined;
  foundValue: string | undefined;
  isNew: boolean;       // campo vazio no atual, preenchido no encontrado
  isDifferent: boolean;  // ambos preenchidos mas valores diferentes
}

// Campos comparáveis por tipo de obra
const COMPARABLE_FIELDS: Record<string, string> = {
  title: 'Título',
  year: 'Ano',
  doi: 'DOI',
  url: 'URL',
  publisher: 'Editora',
  journal: 'Periódico',
  volume: 'Volume',
  issue: 'Número',
  pages: 'Páginas',
  isbn: 'ISBN',
  issn: 'ISSN',
  city: 'Cidade',
  edition: 'Edição',
  university: 'Universidade',
  department: 'Departamento',
  institution: 'Instituição',
  event: 'Evento',
  abstract: 'Resumo',
};

// ============================================================================
// CAMADA 3 — Detecção de status de metadados (3 níveis)
// ============================================================================

/**
 * Detecta o status dos metadados de uma referência.
 *
 * - 'critical': quebra o ABNT — faltam título, autores ou ano
 * - 'warning': referência funciona mas incompleta — campos obrigatórios por tipo
 * - 'complete': todos os campos esperados estão preenchidos
 */
export function detectMetadataStatus(ref: Work): MetadataStatus {
  // CRÍTICO — quebra o ABNT
  const criticalMissing = [
    !ref.title?.trim(),
    !ref.authors || ref.authors.length === 0 || (!ref.authors[0]?.lastName && !ref.authors[0]?.firstName),
    !ref.year,
  ];
  if (criticalMissing.some(Boolean)) return 'critical';

  // IMPORTANTE — referência funciona mas incompleta para ABNT
  const importantMissing: boolean[] = [];

  switch (ref.type) {
    case 'article':
      importantMissing.push(!ref.journal, !ref.volume, !ref.pages);
      break;
    case 'book':
      importantMissing.push(!ref.publisher);
      break;
    case 'chapter':
      importantMissing.push(!ref.bookTitle, !ref.publisher, !ref.pages);
      break;
    case 'thesis':
      importantMissing.push(!ref.university, !ref.thesisType);
      break;
    case 'conference':
      importantMissing.push(!ref.event);
      break;
    case 'report':
      importantMissing.push(!ref.institution);
      break;
  }

  if (importantMissing.some(Boolean)) return 'warning';

  return 'complete';
}

/**
 * Detecta campos faltantes em uma referência.
 * Retorna array de nomes de campos que estão vazios.
 */
export function detectIncompleteFields(ref: Work): string[] {
  const missing: string[] = [];

  // Campos críticos
  if (!ref.title?.trim()) missing.push('title');
  if (!ref.authors || ref.authors.length === 0 || (!ref.authors[0]?.lastName && !ref.authors[0]?.firstName)) {
    missing.push('authors');
  }
  if (!ref.year) missing.push('year');

  // Campos por tipo
  switch (ref.type) {
    case 'article':
      if (!ref.journal) missing.push('journal');
      if (!ref.volume) missing.push('volume');
      if (!ref.pages) missing.push('pages');
      break;
    case 'book':
      if (!ref.publisher) missing.push('publisher');
      break;
    case 'chapter':
      if (!ref.bookTitle) missing.push('bookTitle');
      if (!ref.publisher) missing.push('publisher');
      if (!ref.pages) missing.push('pages');
      break;
    case 'thesis':
      if (!ref.university) missing.push('university');
      if (!ref.thesisType) missing.push('thesisType');
      break;
    case 'conference':
      if (!ref.event) missing.push('event');
      break;
    case 'report':
      if (!ref.institution) missing.push('institution');
      break;
  }

  return missing;
}

// ============================================================================
// CAMADA 1 — Verificar metadados (busca externa + comparação)
// ============================================================================

/**
 * Busca metadados atualizados de fontes externas.
 * Hierarquia: DOI → ISBN → título+autores (CrossRef) → título+autores (OpenAlex)
 */
export async function verifyMetadata(ref: Work): Promise<MetadataComparison | null> {
  let found: Partial<Work> | null = null;
  let source = '';

  // 1. Se tem DOI, buscar via CrossRef (mais confiável)
  if (ref.doi) {
    const candidate = await crossrefSearchByDOI(ref.doi);
    if (candidate) {
      found = candidateToPartialWork(candidate);
      source = 'CrossRef';
    }
  }

  // 2. Se tem ISBN, buscar via Open Library
  if (!found && ref.isbn) {
    const result = await fetchByISBN(ref.isbn);
    if (result.success && result.data) {
      found = metadataResultToPartialWork(result.data);
      source = 'Open Library';
    }
  }

  // 3. Buscar por título + autor via CrossRef
  if (!found && ref.title && ref.authors?.length > 0) {
    const authorName = ref.authors[0].lastName || ref.authors[0].firstName || '';
    if (authorName) {
      const candidates = await searchByAuthorYearTitle(authorName, ref.year || null, ref.title);
      if (candidates.length > 0) {
        const best = candidates[0];
        if (best.relevanceScore >= 60) {
          found = candidateToPartialWork(best);
          source = 'CrossRef';
        }
      }
    }
  }

  // 4. Buscar por título + autor via OpenAlex
  if (!found && ref.title && ref.authors?.length > 0) {
    const authorName = ref.authors[0].lastName || ref.authors[0].firstName || '';
    if (authorName) {
      const candidates = await openAlexSearchByTitleAuthor(ref.title, authorName, ref.year || null);
      if (candidates.length > 0) {
        const best = candidates[0];
        if (best.relevanceScore >= 60) {
          found = candidateToPartialWork(best);
          source = 'OpenAlex';
        }
      }
    }
  }

  if (!found) return null;

  return buildComparison(ref, found, source);
}

/**
 * Constrói a comparação campo-a-campo entre referência atual e dados encontrados.
 * Retorna apenas campos que diferem ou são novos.
 */
export function buildComparison(
  current: Work,
  found: Partial<Work>,
  source: string
): MetadataComparison {
  const fields: ComparisonField[] = [];

  for (const [key, label] of Object.entries(COMPARABLE_FIELDS)) {
    const currentVal = getFieldAsString(current, key);
    const foundVal = getFieldAsString(found as Work, key);

    // Pular se o encontrado está vazio
    if (!foundVal) continue;

    const isNew = !currentVal && !!foundVal;
    const isDifferent = !!currentVal && !!foundVal && normalizeForComparison(currentVal) !== normalizeForComparison(foundVal);

    if (isNew || isDifferent) {
      fields.push({
        key,
        label,
        currentValue: currentVal || undefined,
        foundValue: foundVal,
        isNew,
        isDifferent,
      });
    }
  }

  return { source, fields };
}

// ============================================================================
// CAMADA 2 — Auto-fill (apenas campos vazios, sem sobrescrever)
// ============================================================================

/**
 * Verifica metadados e retorna apenas os campos que podem ser preenchidos automaticamente
 * (campos atualmente vazios que foram encontrados na fonte externa).
 * Usado por "Completar todas".
 */
export async function verifyAndAutoFill(ref: Work): Promise<{
  updates: Partial<Work>;
  fieldsUpdated: number;
  source: string;
} | null> {
  const comparison = await verifyMetadata(ref);
  if (!comparison || comparison.fields.length === 0) return null;

  const updates: Partial<Work> = {};
  let fieldsUpdated = 0;

  for (const field of comparison.fields) {
    // Apenas preencher campos vazios — nunca sobrescrever
    if (field.isNew && field.foundValue) {
      if (field.key === 'year') {
        (updates as Record<string, unknown>)[field.key] = parseInt(field.foundValue, 10);
      } else {
        (updates as Record<string, unknown>)[field.key] = field.foundValue;
      }
      fieldsUpdated++;
    }
  }

  if (fieldsUpdated === 0) return null;

  return {
    updates,
    fieldsUpdated,
    source: comparison.source,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

function getFieldAsString(work: Work | Partial<Work>, key: string): string | undefined {
  const value = (work as Record<string, unknown>)[key];
  if (value === undefined || value === null || value === '') return undefined;
  return String(value);
}

function normalizeForComparison(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?]+$/g, '');
}

function candidateToPartialWork(candidate: WorkCandidate): Partial<Work> {
  const result: Partial<Work> = {};

  if (candidate.title) result.title = candidate.title;
  if (candidate.year) result.year = candidate.year;
  if (candidate.doi) result.doi = candidate.doi;
  if (candidate.journal) result.journal = candidate.journal;
  if (candidate.publisher) result.publisher = candidate.publisher;
  if (candidate.volume) result.volume = candidate.volume;
  if (candidate.issue) result.issue = candidate.issue;
  if (candidate.pages) result.pages = candidate.pages;
  if (candidate.abstract) result.abstract = candidate.abstract;
  if (candidate.url) result.url = candidate.url;

  return result;
}

function metadataResultToPartialWork(data: Record<string, unknown>): Partial<Work> {
  const result: Partial<Work> = {};

  if (data.title) result.title = String(data.title);
  if (data.year) result.year = Number(data.year);
  if (data.doi) result.doi = String(data.doi);
  if (data.publisher) result.publisher = String(data.publisher);
  if (data.isbn) result.isbn = String(data.isbn);
  if (data.url) result.url = String(data.url);

  return result;
}
